import React, { useEffect, useState } from "react";
import { Upload, Button } from "antd";
import * as httpClient from "../../util/httpClient";
import { useDispatch, useSelector } from "react-redux";
import * as secureStorageActions from "../../store/secureStorageService/actions";
import { ApplicationState } from "../../store";
import { VaultFile } from "../../store/secureStorageService/types";
import ConfirmDeleteWalletDialog from "../Dialogs/lostWallet/ConfirmDeleteFilesDialog";
import { DialogComponent } from "../common/DialogComponent";
import { Notification } from "../common/Notification";
import { useTranslation } from "react-i18next";
import { getFormattedDate } from "../../util/dateFormatting";
import i18nService from "../../util/Internationalization/i18n/i18n";
import RenderIcons from "../../Assets/css/_icons/partner_icon/RenderIcons";
import DownloadNotSupportedDialog from "../Dialogs/lostWallet/DownloadNotSupportedDialog";
import { useAreServicesActive } from "../../util/CommonServices";
import { ezsServiceIds } from "../../config/UserServicesList";

const DocumentsAndImages = () => {
    const { t: tLang, i18n: i18nLang } = useTranslation();
    const dispatch = useDispatch();
    const requestData = () => dispatch(secureStorageActions.actionCreators.getDocumentsAndImagesData());
    const vaultFileList = useSelector((state: ApplicationState) => (state.documentsAndImagesState ? state.documentsAndImagesState.vaultFileList : []));
    const isLoading = useSelector((state: ApplicationState) => (state.documentsAndImagesState ? state.documentsAndImagesState.isLoading : false));
    const totalFileSize = useSelector((state: ApplicationState) => (state.documentsAndImagesState ? state.documentsAndImagesState.totalFileSize : 0));

    const [deleteFileDialogIsVisible, setDeleteFileDialogIsVisible] = useState(false);
    const [isNotSupportVisible, setIsNotSupportVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const isSafari = /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);

    const showDeleteFileDialog = (selectedItem: VaultFile): any => {
        setSelectedItem(selectedItem);
        setDeleteFileDialogIsVisible(true);
    };

    const closeDeleteFileDialog = (): any => {
        setDeleteFileDialogIsVisible(false);
    };

    const closeNotSupportDialog = (): any => {
        setIsNotSupportVisible(false);
    };

    useEffect(() => {
        requestData();
    }, []);

    const download = (vaultFile: VaultFile) => {
        const rqstBody = {
            "vault-id": vaultFile.id,
        };
        if (isSafari) {
            setIsNotSupportVisible(true);
        } else {
            Notification({
                type: "info",
                title: "Download",
                message: "Please wait for a moment. Your download will start soon.",
            });
            httpClient
                .sendRequest("POST", `api/securevault/downloadSecureFile`, rqstBody)
                .then((data: any) => {
                    const ua = window.navigator.userAgent;
                    const msie = ua.indexOf("MSIE ");
                    // If Internet Explorer:
                    if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./)) {
                        const contentType = "application/octet-stream";
                        const sliceSize = 1024;
                        const byteCharacters = atob(data.fileContent);

                        const bytesLength = byteCharacters.length;
                        const slicesCount = Math.ceil(bytesLength / sliceSize);
                        const byteArrays = new Array(slicesCount);

                        for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
                            const begin = sliceIndex * sliceSize;
                            const end = Math.min(begin + sliceSize, bytesLength);

                            const bytes = new Array(end - begin);
                            for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
                                bytes[i] = byteCharacters[offset].charCodeAt(0);
                            }
                            byteArrays[sliceIndex] = new Uint8Array(bytes);
                        }
                        const blob = new Blob(byteArrays, { type: contentType });

                        window.navigator.msSaveBlob(blob, vaultFile.label);
                    } else {
                        //other browser
                        var a = document.createElement("a");
                        a.href = "data:application/octet-stream;base64," + data.fileContent;
                        a.download = vaultFile.label ? vaultFile.label : "file1.png";
                        a.click();
                    }
                })
                .catch(function (error: any) {
                    Notification({
                        type: "error",
                        title: "Error",
                        message: "Something went wrong, Please try again.",
                        duration: 0,
                    });
                });
        }
    };

    const toBase64 = (file: any) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () =>
                resolve(
                    reader.result
                        ? reader.result
                              .toString()
                              .replace(/^data:;base64,/, "")
                              .toString()
                              .replace(/^data:.+;base64,/, "")
                        : ""
                );
            reader.onerror = (error) => reject(error);
        });

    const validateFile = (file: any): boolean => {
        let isValid = true;
        if (file.size > 10240000) {
            isValid = false;
            Notification({
                type: "error",
                title: "Error",
                message: "File size can not be more than 10MB.",
                duration: 0,
            });
        } else if ((file.size + totalFileSize) / 1024 / 1024 > 100) {
            isValid = false;
            Notification({
                type: "error",
                title: "Error",
                message: "The file you are trying to add would exceed your total storage limit of 100MB.",
                duration: 0,
            });
        } else if (
            !file.name.toLowerCase().endsWith(".pdf") &&
            !file.name.toLowerCase().endsWith(".png") &&
            !file.name.toLowerCase().endsWith(".jpg") &&
            !file.name.toLowerCase().endsWith(".jpeg") &&
            !file.name.toLowerCase().endsWith(".gif") &&
            !file.name.toLowerCase().endsWith(".doc") &&
            !file.name.toLowerCase().endsWith(".docx") &&
            !file.name.toLowerCase().endsWith(".xls") &&
            !file.name.toLowerCase().endsWith(".xlsx") &&
            !file.name.toLowerCase().endsWith(".ppt") &&
            !file.name.toLowerCase().endsWith(".pptx")
        ) {
            isValid = false;
            Notification({
                type: "error",
                title: "Error",
                message: "Only the following file types are supported for file upload: .png, .jpeg, .jpg, .gif, .doc, .docx, .xls, .xlsx, .ppt, .pptx, and .pdf.",
                duration: 0,
            });
        }

        return isValid;
    };

    const uploadFile = async (data: any) => {
        // validate file
        if (!validateFile(data.file)) {
            return;
        }

        const fileContent = await toBase64(data.file);
        const today = new Date();

        const rqstBody = {
            VaultFileInfo: {
                FileContent: fileContent,
                Label: data.file.name,
                CreatedDate: today.toISOString(),
            },
        };
        setIsUploading(true);
        httpClient
            .sendRequest("POST", `api/securevault/secureUploadFiles`, rqstBody)
            .then((data: any) => {
                setIsUploading(false);
                Notification({
                    type: "success",
                    title: tLang("identityVault.secureStorage.documentsAndImagesWidget.fileUploadToastTitle"),
                    message: tLang("identityVault.secureStorage.documentsAndImagesWidget.fileUploadToastMessaage"),
                });
                requestData();
            })
            .catch(function (error: any) {
                setIsUploading(false);
                Notification({
                    type: "error",
                    title: tLang("identityVault.secureStorage.documentsAndImagesWidget.fileUploadErrorToastTitle"),
                    message: tLang("identityVault.secureStorage.documentsAndImagesWidget.fileUploadErrorToastMessaage"),
                    duration: 0,
                });
            });
    };

    const getUsedMB = () => {
        return totalFileSize ? totalFileSize / 1024 / 1024 : 0;
    };

    const RestorationProActiveServices = useAreServicesActive([ezsServiceIds.businessIdRestorationPro]);

    return (
        <React.Fragment>
            {RestorationProActiveServices && (
                <div className="box offset-padding">
                    <div className="box-header">
                        <div className="box-title-container">
                            <div className="box-title-icon" aria-hidden="true">
                                <RenderIcons iconName="file" />
                            </div>
                            <div className="box-title-text">
                                <h2 className="box-title">{tLang("identityVault.secureStorage.documentsAndImagesWidget.newtitle")}</h2>
                            </div>
                        </div>
                        <div className="box-description">
                            <p>{tLang("identityVault.secureStorage.documentsAndImagesWidget.newmessage")}</p>
                        </div>
                    </div>
                    <div className="box-content">
                        <div className="row between-xs">
                            <div className="col-xs col-no-gutter flex-grow-null">
                                <h3 className="content-sub-title">
                                    {tLang("identityVault.secureStorage.documentsAndImagesWidget.newfilesListTitle")}{" "}
                                    {vaultFileList && vaultFileList.length >= 0 && <span>({vaultFileList.length}/100)</span>}
                                </h3>
                                <p>
                                    <RenderIcons iconName="info-circle" />
                                    &nbsp;
                                    {vaultFileList && vaultFileList.length >= 0 && (
                                        <span>
                                            {getUsedMB().toFixed(2) === "100.00" ? "100" : getUsedMB().toFixed(2)}MB{" "}
                                            {tLang("identityVault.secureStorage.documentsAndImagesWidget.100MBUsedText")}
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div className="col-xs col-no-gutter flex-grow-null">
                                <Upload
                                    disabled={isUploading || getUsedMB().toFixed(2) === "100.00" || (vaultFileList && vaultFileList.length > 99)}
                                    customRequest={uploadFile}
                                    showUploadList={false}
                                    accept={".png,.jpeg,.jpg,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf"}
                                >
                                    <Button
                                        className="btn-default btn-primary"
                                        disabled={isUploading || getUsedMB().toFixed(2) === "100.00" || (vaultFileList && vaultFileList.length > 99)}
                                    >
                                        {isUploading
                                            ? tLang("identityVault.secureStorage.documentsAndImagesWidget.UploadingButtonText")
                                            : tLang("identityVault.secureStorage.documentsAndImagesWidget.UploadFileButtonText")}{" "}
                                        {isUploading && <i className="las la-circle-notch la-spin"></i>}
                                    </Button>
                                </Upload>
                            </div>
                        </div>
                        {!isLoading ? (
                            <>
                                {vaultFileList && vaultFileList.length < 1 && (
                                    <div className="msg-generic msg-small margin-top-xs">
                                        <RenderIcons iconName="info-circle" /> {tLang("identityVault.secureStorage.documentsAndImagesWidget.newnoItemMessage")}
                                    </div>
                                )}
                                <div role={vaultFileList.length ? "list" : ''} className="data-grid has-lines margin-top-s" id="your-documents-grid">
                                    {vaultFileList &&
                                        vaultFileList.map((vaultFile: VaultFile, index) => (
                                            <div role="listitem" key={"vault-item-" + index} className="data-row">
                                                <div className="document-container">
                                                    <a href="javascript:void(0);" className="document-link" onClick={() => download(vaultFile)}>
                                                        <span className="document-icon">
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".pdf") && (
                                                                <em className="las la-file-pdf"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".png") && (
                                                                <em className="las la-image"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".jpg") && (
                                                                <em className="las la-image"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".jpeg") && (
                                                                <em className="las la-image"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".gif") && (
                                                                <em className="las la-image"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".doc") && (
                                                                <em className="las la-file-word"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".docx") && (
                                                                <em className="las la-file-word"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".xls") && (
                                                                <em className="las la-file-excel"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".xlsx") && (
                                                                <em className="las la-file-excel"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".ppt") && (
                                                                <em className="las la-file-powerpoint"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".pptx") && (
                                                                <em className="las la-file-powerpoint"></em>
                                                            )}
                                                        </span>
                                                        <span className="document-text">
                                                            <span className="document-title">{vaultFile.label}</span>
                                                            <span className="document-meta">
                                                                <span className="sr-only">Date Added: </span>
                                                                {vaultFile.createdDate && getFormattedDate(i18nService.get("Country"), vaultFile.createdDate)}
                                                            </span>
                                                        </span>
                                                    </a>
                                                    <div className="document-actions">
                                                        <button
                                                            type="button"
                                                            className="btn-default btn-secondary-outline btn-icon-only btn-no-border btn-no-padding btn-remove-item"
                                                            onClick={() => showDeleteFileDialog(vaultFile)}
                                                        >
                                                            <span className="btn-icon">
                                                                <em className="las la-trash-alt"></em>
                                                            </span>
                                                            <span className="btn-text">{vaultFile.label}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </>
                        ) : (
                            <div className="loading"></div>
                        )}
                    </div>

                    {/* Delete modal*/}
                    {deleteFileDialogIsVisible && (
                        <DialogComponent dialogId="dialog-delete-bank-account-wallet" title="Delete Wallet" closeDialog={closeDeleteFileDialog}>
                            <ConfirmDeleteWalletDialog closeDialog={closeDeleteFileDialog} fileItem={selectedItem} />
                        </DialogComponent>
                    )}
                    {isNotSupportVisible && (
                        <div className="dialog-center">
                            <DialogComponent dialogId="dialog-download-not-support" title="Download Not Supported" closeDialog={closeNotSupportDialog}>
                                <DownloadNotSupportedDialog closeDialog={closeNotSupportDialog} />
                            </DialogComponent>
                        </div>
                    )}
                </div>
            )}

            {!RestorationProActiveServices && (
                <div className="box offset-padding">
                    <div className="box-header">
                        <div className="box-title-container">
                            <div className="box-title-icon" aria-hidden="true">
                                <RenderIcons iconName="file" />
                            </div>
                            <div className="box-title-text">
                                <h2 className="box-title">{tLang("identityVault.secureStorage.documentsAndImagesWidget.title")}</h2>
                            </div>
                        </div>
                        <div className="box-description">
                            <p>{tLang("identityVault.secureStorage.documentsAndImagesWidget.message")}</p>
                        </div>
                    </div>
                    <div className="box-content">
                        <div className="row between-xs">
                            <div className="col-xs col-no-gutter flex-grow-null">
                                <h3 className="content-sub-title">
                                    {tLang("identityVault.secureStorage.documentsAndImagesWidget.filesListTitle")}{" "}
                                    {vaultFileList && vaultFileList.length >= 0 && <span>({vaultFileList.length}/100)</span>}
                                </h3>
                                <p>
                                    <RenderIcons iconName="info-circle" />
                                    &nbsp;
                                    {vaultFileList && vaultFileList.length >= 0 && (
                                        <span>
                                            {getUsedMB().toFixed(2) === "100.00" ? "100" : getUsedMB().toFixed(2)}MB{" "}
                                            {tLang("identityVault.secureStorage.documentsAndImagesWidget.100MBUsedText")}
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div className="col-xs col-no-gutter flex-grow-null">
                                <Upload
                                    disabled={isUploading || getUsedMB().toFixed(2) === "100.00" || (vaultFileList && vaultFileList.length > 99)}
                                    customRequest={uploadFile}
                                    showUploadList={false}
                                    accept={".png,.jpeg,.jpg,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf"}
                                >
                                    <Button
                                        className="btn-default btn-primary"
                                        disabled={isUploading || getUsedMB().toFixed(2) === "100.00" || (vaultFileList && vaultFileList.length > 99)}
                                    >
                                        {isUploading
                                            ? tLang("identityVault.secureStorage.documentsAndImagesWidget.UploadingButtonText")
                                            : tLang("identityVault.secureStorage.documentsAndImagesWidget.UploadFileButtonText")}{" "}
                                        {isUploading && <i className="las la-circle-notch la-spin"></i>}
                                    </Button>
                                </Upload>
                            </div>
                        </div>

                        {!isLoading ? (
                            <>
                                {vaultFileList && vaultFileList.length < 1 && (
                                    <div className="msg-generic msg-small margin-top-xs">
                                        <RenderIcons iconName="info-circle" /> {tLang("identityVault.secureStorage.documentsAndImagesWidget.newnoItemMessage")}
                                    </div>
                                )}
                                <div role={vaultFileList.length ? "list" : ''} className="data-grid has-lines margin-top-s" id="your-documents-grid">
                                    {vaultFileList &&
                                        vaultFileList.map((vaultFile: VaultFile, index) => (
                                            <div role="listitem" key={"vault-item-" + index} className="data-row">
                                                <div className="document-container">
                                                    <a href="javascript:void(0);" className="document-link" onClick={() => download(vaultFile)}>
                                                        <span className="document-icon">
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".pdf") && (
                                                                <em className="las la-file-pdf"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".png") && (
                                                                <em className="las la-image"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".jpg") && (
                                                                <em className="las la-image"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".jpeg") && (
                                                                <em className="las la-image"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".gif") && (
                                                                <em className="las la-image"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".doc") && (
                                                                <em className="las la-file-word"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".docx") && (
                                                                <em className="las la-file-word"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".xls") && (
                                                                <em className="las la-file-excel"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".xlsx") && (
                                                                <em className="las la-file-excel"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".ppt") && (
                                                                <em className="las la-file-powerpoint"></em>
                                                            )}
                                                            {vaultFile.label && vaultFile.label.toLocaleLowerCase().endsWith(".pptx") && (
                                                                <em className="las la-file-powerpoint"></em>
                                                            )}
                                                        </span>
                                                        <span className="document-text">
                                                            <span className="document-title">{vaultFile.label}</span>
                                                            <span className="document-meta">
                                                                <span className="sr-only">Date Added: </span>
                                                                {vaultFile.createdDate && getFormattedDate(i18nService.get("Country"), vaultFile.createdDate)}
                                                            </span>
                                                        </span>
                                                    </a>
                                                    <div className="document-actions">
                                                        <button
                                                            type="button"
                                                            className="btn-default btn-secondary-outline btn-icon-only btn-no-border btn-no-padding btn-remove-item"
                                                            onClick={() => showDeleteFileDialog(vaultFile)}
                                                        >
                                                            <span className="btn-icon">
                                                                <em className="las la-trash-alt"></em>
                                                            </span>
                                                            <span className="btn-text">{vaultFile.label}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </>
                        ) : (
                            <div className="loading"></div>
                        )}
                    </div>

                    {/* Delete modal*/}
                    {deleteFileDialogIsVisible && (
                        <DialogComponent dialogId="dialog-delete-bank-account-wallet" title="Delete Wallet" closeDialog={closeDeleteFileDialog}>
                            <ConfirmDeleteWalletDialog closeDialog={closeDeleteFileDialog} fileItem={selectedItem} />
                        </DialogComponent>
                    )}
                    {isNotSupportVisible && (
                        <div className="dialog-center">
                            <DialogComponent dialogId="dialog-download-not-support" title="Download Not Supported" closeDialog={closeNotSupportDialog}>
                                <DownloadNotSupportedDialog closeDialog={closeNotSupportDialog} />
                            </DialogComponent>
                        </div>
                    )}
                </div>
            )}
        </React.Fragment>
    );
};

export default DocumentsAndImages;
